"use client";

import { Button } from "@/components/atoms/Button";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { ArrowUpRight, Sparkles } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

interface FeatureRow {
  id?: string | number;
  icon?: string;
  alt_icon?: string;
  title?: string;
  description?: string;
  priority?: number;
  status?: boolean;
}

interface BgRow {
  id?: string | number;
  image?: string;
  mobile_image?: string;
  alt_image?: string;
  mobile_alt_image?: string;
  priority?: number;
  status?: boolean;
}

interface Banner5Data {
  metadata?: FeatureRow[];
  metadata_2?: BgRow[];
  background_image?: string;
  alt_background_image?: string;
  title_1?: string;
  title_1_color?: string;
  subtitle?: string;
  subtitle_color?: string;
  learners_description?: string;
  icon_color?: string;
  button_color?: string;
  apply_now_text?: string;
  apply_now_color?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  background_color?: string;
  hero_section_height?: number;
  hero_container_max_width?: number;
  status?: boolean;
}

function pickBgRow(rows: BgRow[] | undefined): BgRow | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const active = rows
    .filter((r) => r !== null && r !== undefined && r.status !== false)
    .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0));
  return active[0] ?? null;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function stripHtml(s: string): string {
  return s.replace(/<[^>]+>/g, "").trim();
}

/**
 * Renders headline text with `{…}`-wrapped segments in the accent colour, the
 * rest in baseColor. Braces are stripped from the output.
 * e.g. "Build One System. {Ship to Production}" → accent on "Ship to Production".
 */
function renderAccentBraces(
  text: string,
  baseColor: string,
  accentColor: string,
) {
  return text.split(/(\{[^}]*\})/g).map((part, i) => {
    if (!part) return null;
    const braced = part.length > 1 && part[0] === "{" && part.endsWith("}");
    return (
      <span key={i} style={{ color: braced ? accentColor : baseColor }}>
        {braced ? part.slice(1, -1) : part}
      </span>
    );
  });
}

function ParagraphHtml({ htmlOrText }: { htmlOrText: string }) {
  const t = htmlOrText.trim();
  if (!t) return null;
  if (t.includes("<") && t.includes(">")) {
    return (
      <div
        className="text-body-4-regular min-[1024px]:text-body-3-regular min-[1024px]:font-normal text-white min-[1024px]:text-left text-center"
        style={{ color: "inherit" }}
        dangerouslySetInnerHTML={{ __html: t }}
      />
    );
  }
  return (
    <p className="whitespace-pre-wrap text-body-4-regular min-[1024px]:text-body-3-regular min-[1024px]:font-normal min-[1024px]:text-left text-center">
      {t}
    </p>
  );
}

const Banner5 = (props: { data?: Banner5Data }) => {
  const { data: sectionData } = props;
  const bannerRef = useRef<HTMLElement | null>(null);
  const dispatch = useDispatch();

  const {
    metadata = [],
    metadata_2 = [],
    title_1 = "",
    title_1_color = "#FFFFFF",
    subtitle = "",
    subtitle_color = "rgba(255,255,255,0.92)",
    learners_description = "",
    icon_color = "#F5C451",
    button_color = "#E85D04",
    apply_now_text = "Apply Now",
    apply_now_color = "#FFFFFF",
    apply_now_link = "",
    background_color = "#0a0a0a",
    background_image = "",
    alt_background_image = "",
    hero_section_height = 0,
    hero_container_max_width = 1200,
    status = true,
  } = sectionData || {};

  const bannerRaw = sectionData as Record<string, unknown> | undefined;
  const applyNowIsCta = isCmsShowFlagOn(
    bannerRaw?.apply_now_is_cta ?? bannerRaw?.applyNowIsCta,
    true,
  );
  const ctaHref = String(apply_now_link ?? "").trim()
    ? normalizeHref(String(apply_now_link ?? "").trim())
    : "";
  const primaryOpensLeadPopup = applyNowIsCta || !ctaHref;

  const bgRow = useMemo(() => pickBgRow(metadata_2), [metadata_2]);

  const gridRows = useMemo(() => {
    if (!Array.isArray(metadata)) return [];
    return [...metadata]
      .filter((r) => r && r.status !== false)
      .sort(
        (a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0),
      );
  }, [metadata]);

  /**
   * `?? ""` is load-bearing, not defensive noise.
   *
   * `background_image` is a VIRTUAL admin field: Banner 6 persists its hero into
   * `metadata_2` (buildBanner6PersistPayload), so the column stays NULL and the public
   * payload ships `background_image: null`. A destructuring default only fires on
   * `undefined` — a NULL sails past it, and `String(null)` is the four-character string
   * "null", which is TRUTHY. That won the `||` chain below and rendered
   * `/_next/image?url=null`, i.e. a broken hero, while the admin preview looked perfect
   * because preparePreviewData flattens metadata_2 into this prop with its own `??`.
   */
  const flatBg = String(background_image ?? "").trim();
  const desktopBg = String(bgRow?.image ?? "").trim();
  const mobileBg = String(bgRow?.mobile_image ?? "").trim();
  const bgSrc = flatBg || desktopBg || mobileBg;
  const heroImgAlt =
    String(alt_background_image ?? "").trim() ||
    String(bgRow?.alt_image || bgRow?.mobile_alt_image || "").trim() ||
    "banner-bg";

  const contentMax = Number(hero_container_max_width) || 1200;
  const rawMinH = Number(hero_section_height);
  const minHeightStyle =
    Number.isFinite(rawMinH) && rawMinH > 0 ? { minHeight: rawMinH } : undefined;

  const learnersHtml =
    learners_description.trim().includes("<") &&
    learners_description.includes(">");

  const combinedBodyPlain = useMemo(() => {
    const a = subtitle.trim();
    const b = learners_description.trim();
    if (!b) return a;
    if (learnersHtml) return a;
    return [a, stripHtml(b)].filter(Boolean).join("\n\n");
  }, [subtitle, learners_description, learnersHtml]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   FireWidgetViewedEvent(bannerRef, "banner_5");
  // }, []);

  const handleApplyLeadPopup = useCallback(() => {
    ctaClick({
      section: "banner_6",
      component: "capstone_apply_now",
      label: apply_now_text?.trim() || "apply_now",
      step: "1",
      entity: "banner_6",
      parentEntity: "banner_6",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [apply_now_text, dispatch]);

  if (!status) return null;

  const accent = icon_color?.trim() || "#F5C451";

  return (
    <section
      ref={bannerRef}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative w-full min-w-0 overflow-hidden",
      )}
      style={{
        ...minHeightStyle,
        // When a hero image is set: no fill color, no tint — only the photo (z-0, not -z-10, so parent bg doesn’t show through).
        ...(bgSrc
          ? { backgroundColor: "transparent" }
          : { backgroundColor: background_color }),
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {bgSrc ? (
        <div
          className="pointer-events-none absolute inset-0 z-0 h-full w-full"
          aria-hidden
        >
          <Image
            alt={heroImgAlt}
            src={bgSrc}
            fill
            priority
            fetchPriority="high"
            quality={100}
            sizes="100vw"
            className="object-cover object-center"
          />
        </div>
      ) : null}

      <div
        className="relative z-10 mx-auto flex w-full min-w-0 max-w-screen-container flex-col items-center gap-10 px-5 py-11 min-[1024px]:flex-row min-[1024px]:gap-[85px] min-[1024px]:px-10"
        style={
          contentMax && contentMax !== 1114
            ? { maxWidth: Math.min(contentMax, 1920) }
            : undefined
        }
      >
        <div className="flex w-[328px] max-w-full shrink-0 flex-col items-center justify-start gap-6 min-[1024px]:w-[426px] min-[1024px]:items-start">
          <div className="flex w-full flex-col items-center gap-2 min-[1024px]:items-start">
            {title_1 ? (
              <h2
                className="w-full text-center text-[2rem] font-semibold leading-[126%] min-[1024px]:text-left"
              >
                {renderAccentBraces(title_1, title_1_color, accent)}
              </h2>
            ) : null}

            {learnersHtml && subtitle.trim() ? (
              <p
                className="whitespace-pre-wrap text-body-4-regular min-[1024px]:text-body-3-regular min-[1024px]:font-normal min-[1024px]:text-left text-center"
                style={{ color: subtitle_color }}
              >
                {subtitle}
              </p>
            ) : null}
            {learnersHtml && learners_description.trim() ? (
              <div style={{ color: subtitle_color }}>
                <ParagraphHtml htmlOrText={learners_description} />
              </div>
            ) : null}

            {!learnersHtml && combinedBodyPlain ? (
              <p
                className="whitespace-pre-wrap text-body-4-regular min-[1024px]:text-body-3-regular min-[1024px]:font-normal min-[1024px]:text-left text-center"
                style={{ color: subtitle_color }}
              >
                {combinedBodyPlain}
              </p>
            ) : null}
          </div>

          <div className="flex w-full items-center justify-center min-[1024px]:inline-flex min-[1024px]:items-start min-[1024px]:justify-start">
            {primaryOpensLeadPopup ? (
            <Button
              type="button"
              onClick={handleApplyLeadPopup}
              classType="primary"
              className={cn(
                "!box-border !shrink-0 !rounded-md !px-2 !py-0",
                "!gap-2 !text-sm !font-semibold !leading-none",
                "!inline-flex !items-center !justify-center",
              )}
              style={{
                backgroundColor: button_color,
                color: apply_now_color,
                width: "156.56px",
                height: "48px",
                boxSizing: "border-box",
              }}
            >
              {apply_now_text}
              <ArrowUpRight className="size-4 shrink-0" aria-hidden />
            </Button>
            ) : (
              <Link
                href={ctaHref}
                className={cn(
                  "!box-border !shrink-0 !rounded-md !px-2 !py-0",
                  "!gap-2 !text-sm !font-semibold !leading-none",
                  "!inline-flex !items-center !justify-center",
                  "transition-opacity hover:opacity-95 no-underline",
                )}
                style={{
                  backgroundColor: button_color,
                  color: apply_now_color,
                  width: "156.56px",
                  height: "48px",
                  boxSizing: "border-box",
                }}
                onClick={() =>
                  ctaClick({
                    section: "banner_6",
                    component: "capstone_apply_now_link",
                    label: apply_now_text?.trim() || "apply_now",
                    step: "1",
                    entity: "banner_6",
                    parentEntity: "banner_6",
                  })
                }
              >
                {apply_now_text}
                <ArrowUpRight className="size-4 shrink-0" aria-hidden />
              </Link>
            )}
          </div>
        </div>

        {gridRows.length > 0 ? (
          <div className="flex flex-1 justify-center min-[1024px]:my-12">
            <div
              className={cn(
                "flex w-[350px] flex-wrap justify-center gap-x-[14px] gap-y-[30px] min-[1024px]:grid min-[1024px]:w-[450px]",
                "min-[1024px]:grid-cols-2 min-[1024px]:gap-x-10 min-[1024px]:gap-y-[30px]",
              )}
            >
              {gridRows.map((row, i) => {
                const iconSrc = String(row.icon ?? "").trim();
                const label = (row.title ?? "").trim();
                const line = (row.description ?? "").trim();
                const lineText = [label, line].filter(Boolean).join(" : ");
                return (
                  <div
                    key={String(row.id ?? `${label}-${i}`)}
                    className="m-0 flex w-[155px] flex-col items-center justify-start gap-[14px] rounded-xl bg-transparent p-0 shadow-none min-[1024px]:w-[191px] min-[1024px]:items-start min-[1024px]:gap-6"
                  >
                    <div className="relative size-[30px] shrink-0 rounded-lg min-[1024px]:size-9">
                      {iconSrc ? (
                        <Image
                          src={iconSrc}
                          alt={row.alt_icon || lineText || "Feature"}
                          fill
                          className="rounded-lg object-cover"
                          sizes="40px"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center rounded-lg">
                          <Sparkles
                            className="size-[30px] min-[1024px]:size-9"
                            strokeWidth={1.75}
                            style={{ color: accent }}
                            aria-hidden
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-1.5 min-[1024px]:items-start min-[1024px]:gap-3.5">
                      <h3
                        className={cn(
                          "w-[156px] text-center font-semibold leading-normal tracking-tighter text-[#FFFFFF]",
                          "text-body-5-regular min-[1024px]:w-[232px] min-[1024px]:!text-[18px] min-[1024px]:text-left",
                        )}
                        style={{ color: "#FFFFFF" }}
                      >
                        {label ? (
                          <span className="font-semibold text-[#FFFFFF]">
                            {label}
                          </span>
                        ) : null}
                        {label && line ? (
                          <span className="font-semibold text-[#FFFFFF]" aria-hidden>
                            {" "}
                            :{" "}
                          </span>
                        ) : null}
                        {line ? (
                          <span className="font-semibold text-[#FFFFFF] text-[14px] min-[1024px]:text-[18px]">
                            {line}
                          </span>
                        ) : null}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default Banner5;
